<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="addButton"
    @close="closeForm"
    @reset="resetForm"
    @submit.prevent="onSubmit"
  >
    <div class="row q-col-gutter-lg">
      <div v-if="showItemTypeOptions" class="col-12">
        <PField v-model="itemType" label="Type" required>
          <template #control>
            <POptionGroup
              v-model="itemType"
              color="primary-100"
              size="md"
              keep-color
              :options="[
                {
                  label: 'Product',
                  value: 'product',
                },
                {
                  label: 'Freight',
                  value: 'freight',
                },
                {
                  label: 'Packaging',
                  value: 'package',
                },
              ]"
              @update:model-value="resetForm"
            />
          </template>
        </PField>
      </div>
      <div class="col-12">
        <PSelectInput
          v-model="formData"
          :disable="disableItemSelection"
          :emit-value="false"
          :help="computedItemSelectorHelp"
          :map-options="false"
          :options="unref(productFilter.entities)"
          :options-loading="unref(productFilter.loading)"
          :rules="[validateDuplicateServiceItem]"
          :validations="{
            key: 'item_id',
            required: true,
          }"
          input-debounce="300"
          :label="computedSelectLabel"
          option-label="name"
          option-value="item_id"
          :placeholder="computedSelectLabel"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @filter="onProductFilter"
          @filter-abort="productFilter.onFilterAbort"
          @virtual-scroll="productFilter.onScroll"
          @update:model-value="populateItemData"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
              <q-item-section>
                <q-item-label class="text-neutral text-sm">
                  {{ opt.name }}
                </q-item-label>
                <q-item-label
                  v-if="
                    [
                      'Credit Note',
                      'Debit Note',
                      'Proof of Delivery',
                      'Quality Check',
                    ].includes(entity.type)
                  "
                  class="text-neutral-70"
                  caption
                  lines="1"
                >
                  Rate(exc tax)
                  {{
                    numberFormat({
                      mfd: 3,
                      number: opt.rate,
                      style: "currency",
                    })
                  }}/{{ opt.unit }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>
      <div :class="[entity.type === 'Proof of Delivery' ? 'col-12' : 'col-4']">
        <PSelectInput
          v-model="formData.unit"
          :disable="disableUnitSelection"
          :help="computedUnitSelectorHelp"
          :options="unref(unitFilter.entities)"
          input-debounce="300"
          label="Unit"
          option-label="name"
          option-value="name"
          placeholder="Select unit"
          required
          @filter="unitFilter.onFilter"
          @filter-abort="unitFilter.onFilterAbort"
          @update:model-value="onUpdateUnit"
        ></PSelectInput>
      </div>
      <div v-if="showHsnInput" class="col-4">
        <PInput
          v-model.trim.number="formData.hsn"
          label="HSN"
          placeholder="Enter HSN code"
          type="number"
          :validations="{
            type: 'integer',
            lengths: [6, 8],
            required: true,
          }"
          disable
          @keydown.space.prevent
        ></PInput>
      </div>
      <div v-if="showGstInput" :class="gstInputWrapperClasses">
        <PInput
          v-model.trim.number="formData.gst"
          :disable="disableGstInput"
          :help="computedGstInputHelp"
          label="GST (%)"
          option-label="label"
          option-value="value"
          required
          type="number"
          min="0"
          max="100"
          @update:model-value="updateAmount"
        />
      </div>
      <div v-if="showCessInput" :class="cessInputWrapperClasses">
        <PInput
          v-model.trim.number="computedCess"
          :disable="disableCessInput"
          :help="computedCessInputHelp"
          :validations="{
            type: 'integer',
            gte: 0,
            lte: 40,
          }"
          label="CESS (%)"
          min="0"
          max="40"
          type="number"
          required
          @keydown.space.prevent
        >
        </PInput>
      </div>
      <div
        v-if="!(showGstInput || showCessInput) && showHsnInput"
        class="col-4"
      ></div>
      <div v-if="showRateInput" class="col-4">
        <PInput
          v-model.trim.number="computedRate"
          :disable="disableRateInput"
          :help="computedRateInputHelp"
          :label="
            ['Credit Note', 'Debit Note', 'Quality Check'].includes(
              props.entity.type,
            )
              ? 'Rate (Excl. Tax)'
              : 'Rate'
          "
          :validations="{
            gt: computedMinRate,
            lte: computedMaxVal,
            required: true,
            type: 'float',
          }"
          class="prepend-bg"
          :min="computedMinRate"
          placeholder="Enter rate"
          step="any"
          type="number"
          @keydown.space.prevent
          @update:model-value="updateAmount"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template>
        </PInput>
        <q-checkbox
          v-if="showIncTaxCheckBox"
          v-model="formData.is_tax_included"
          label="Inc. Tax?"
          size="xs"
          :disable="disableIncTaxCheckBox"
          @update:model-value="updateRate"
        />
        <p v-if="showActualRate" class="q-mb-none q-mt-xs text-neutral-70">
          Actual Rate: <br />
          <span>
            {{
              numberFormat({
                mfd: 10,
                number: computedActualRate,
                style: "currency",
              })
            }}
          </span>
        </p>
      </div>
      <div :class="[entity.type === 'Proof of Delivery' ? 'col-12' : 'col-4']">
        <PInput
          v-model.trim.number="formData.qty"
          :disable="disableQuantityInput"
          :help="computedQtyInputHelp"
          :max="maxQty"
          :min="minQty"
          :validations="{
            gt: minQty,
            lte: maxQty,
            required: true,
            type: 'float',
          }"
          label="Quantity"
          placeholder="Enter value"
          step="any"
          type="number"
          @keydown.space.prevent
          @update:model-value="updateAmount"
        />
      </div>
      <div v-if="showAmountInput" class="col-4">
        <PInput
          v-model.trim.number="computedAmount"
          :disable="disableAmountInput"
          :help="computedAmountInputHelp"
          :label="
            ['Credit Note', 'Debit Note', 'Quality Check'].includes(
              props.entity.type,
            )
              ? 'Total Amount (Excl. Tax)'
              : 'Total Amount'
          "
          :rules="[() => isReverseCalculated || 'Reverse not calculated']"
          :validations="{
            gte: computedMinAmount,
            lte: computedMaxVal,
            required: true,
            type: 'float',
          }"
          class="prepend-bg"
          :min="computedMinAmount"
          :max="computedMaxVal"
          placeholder="Enter value"
          step="any"
          type="number"
          @keydown.space.prevent
          @update:model-value="onUpdateAmount"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template>
        </PInput>
        <PBtn
          v-if="showReverseCalculateButton"
          label="Reverse Calculate"
          dense
          flat
          text
          @click="reverseCalculate"
        />
      </div>
    </div>
    <q-separator v-if="showPOUnit || showConversionInfo" spaced="xl" />
    <div v-if="showPOUnit" class="row q-col-gutter-lg">
      <div class="col-4">
        <PInput
          v-model="formData.po_unit"
          label="Purchase Order Unit"
          disable
          required
        ></PInput>
      </div>
      <div v-if="showUnitConvertor" class="col-4">
        <PInput
          v-model.trim.number="formData.conversion_factor"
          :validations="{
            gte: 1,
            type: 'float',
            required: true,
          }"
          label="Conversion Factor"
          placeholder="Enter value"
          step="any"
          type="number"
        ></PInput>
      </div>
      <div v-if="showUnitConvertor" class="col-4">
        <PSelectInput
          v-model="isPoUnitBigger"
          :options="[formData.unit, formData.po_unit]"
          input-debounce="300"
          label="Which unit is bigger?"
          placeholder="Select unit"
          required
        ></PSelectInput>
      </div>
    </div>
    <div v-if="showConversionInfo">
      <p>Conversion Factor Info</p>
      <span
        :class="[
          formData.conversion_factor === 1
            ? 'text-neutral-70'
            : 'text-warning-70',
        ]"
        >{{ conversionFactor }}</span
      >
    </div>
  </PForm>
</template>

<script setup>
import itemFormModel from "src/modules/purchase-order/models/itemForm";
import { numberFormat, roundOff } from "assets/scripts/utilities/helpers";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const props = defineProps({
  entity: {
    type: Object,
    default: () => {
      return {};
    },
  },
  index: {
    type: Number,
    default: 0,
  },
  item: {
    type: Object,
    default: () => {
      return itemFormModel()();
    },
  },
  items: {
    type: Array,
    default: () => {
      return [];
    },
  },
  categories: {
    type: Array,
    default: () => {
      return [];
    },
  },
  toggle: {
    type: Object,
    default: () => {
      return {
        method: false,
        options: {},
      };
    },
  },
  source: {
    type: String,
    default: "",
  },
});

const { itemFormState } = storeToRefs(usePurchaseOrderStore());
const { toggleItemForm } = usePurchaseOrderStore();

const pForm = ref(null);

const formHeading = computed(() => {
  if (itemFormState.edit) {
    return "Edit Item";
  } else {
    if (isServiceItem.value) {
      return "Add Service Fee";
    }

    return "Add Item to " + props.entity.type;
  }
});

const item = computed(() => props.item);
const itemType = ref("product");

const isDiscountItem = computed(() => {
  if (unref(itemFormState)?.edit) {
    return unref(item)?.isDiscountItem;
  }

  return props.categories.includes("Discount");
});

const isFreightOrPackageItem = computed(() => {
  return (
    (props.entity.type === "Purchase Bill" &&
      (props.items.length ||
        unref(formData).name.toLowerCase().includes("freight") ||
        unref(formData).name.toLowerCase().includes("package"))) ||
    unref(itemType) != "product"
  );
});

const isServiceItem = computed(() => {
  return props.categories.includes("VAS");
});

const validateDuplicateServiceItem = (serviceItem) => {
  if (unref(isServiceItem)) {
    if (
      props.entity?.vas_items?.find(
        (item) => item.item_id === serviceItem.item_id,
      )
    ) {
      return `This product is already added to ${props.entity?.type}`;
    }
  }

  return true;
};

const { formData, closeForm, resetForm, validateForm } = useForm({
  formModel: itemFormModel(),
  entity: item,
  formRef: pForm,
  isForm: itemFormState,
  toggleForm: toggleItemForm,
});

const { getConvertedQty } = useProductList(props.entity);

const maxQty = computed(() => {
  if (unref(formData).item_id) {
    if (unref(isFreightOrPackageItem)) {
      return 1;
    }

    switch (props.entity.type) {
      case "E-Invoice":
        return getConvertedQty(unref(formData));

      case "Proof of Delivery":
        return unref(formData).invc_qty;

      case "Credit Note":
      case "Debit Note":
        if (props.entity.type_cd !== "returns") {
          return 1;
        }

        if ([props.entity.cn_for, props.entity.dn_for].includes("invoice")) {
          return unref(formData).invc_qty;
        }

        return unref(formData).pb_qty;

      case "Quality Check":
        return 1;

      default:
        return Number.NaN;
    }
  }

  return Number.NaN;
});

const minQty = computed(() => {
  return 0;
});

const computedCess = computed({
  get: () => {
    return Number(unref(formData).cess);
  },
  set: (cess) => {
    unref(formData).cess = cess;
  },
});

const computedIsTaxIncluded = computed(() => {
  if (
    ["Credit Note", "Debit Note", "Quality Check"].includes(props.entity.type)
  ) {
    return false;
  }

  return JSON.parse(unref(formData).is_tax_included ?? "false");
});

const computedRate = computed({
  get() {
    const rate = unref(computedIsTaxIncluded)
      ? unref(formData).rate_wt
      : unref(formData).rate;

    return Number.isNaN(Number(rate)) ? rate : roundOff(Number(rate));
  },
  set(rate) {
    const numRate = Number.isNaN(Number(rate)) ? rate : Number(rate);

    if (unref(computedIsTaxIncluded)) {
      unref(formData).rate_wt = numRate;
    } else {
      unref(formData).rate = numRate;
    }
  },
});

const computedActualRate = computed(() => {
  if (unref(formData).is_tax_included) {
    return unref(formData).rate_wt;
  } else {
    return unref(formData).rate;
  }
});

const computedAmount = computed({
  get() {
    if (unref(computedIsTaxIncluded)) {
      return roundOff(unref(formData).amount_wt ?? 0) || 0;
    } else {
      return roundOff(unref(formData).amount ?? 0) || 0;
    }
  },
  set(amount) {
    if (unref(computedIsTaxIncluded)) {
      unref(formData).amount_wt = amount;
    } else {
      unref(formData).amount = amount;
    }
  },
});

const computedSelectLabel = computed(() => {
  if (props.source) {
    return `Select ${useChangeCase(itemType.value == "package" ? "packaging" : (itemType.value ?? ""), "capitalCase").value}`;
  } else {
    return "Select Product";
  }
});

const computedItemSelectorHelp = computed(() => {
  if (props.entity.type === "Proof of Delivery") {
    return "Pick the item that was returned";
  }

  if (props.entity.type === "Quality Check") {
    return "Pick the item that was rejected";
  }

  return "";
});

const computedUnitSelectorHelp = computed(() => {
  if (props.entity.type === "E-Invoice") {
    if (unref(isDiscountItem) || unref(isServiceItem)) {
      return "";
    }

    return "As per PO";
  }

  if (props.entity.type === "Proof of Delivery") {
    return "As in the Invoice";
  }

  if (unref(formData).item_id) {
    if (["Credit Note", "Debit Note"].includes(props.entity.type)) {
      if ([props.entity?.cn_for, props.entity?.dn_for].includes("invoice")) {
        return "Taken from Invoice";
      }

      return "Taken from PB";
    }

    if (props.entity.type === "Quality Check") {
      return "Taken from Invoice";
    }
  }

  return "";
});

const computedGstInputHelp = computed(() => {
  if (props.entity.type === "E-Invoice") {
    if (unref(isDiscountItem) || unref(isServiceItem)) {
      return "";
    }

    return "As per PO";
  }

  if (unref(formData).item_id) {
    if (["Credit Note", "Debit Note"].includes(props.entity.type)) {
      if ([props.entity?.cn_for, props.entity?.dn_for].includes("invoice")) {
        return "Taken from Invoice";
      }

      return "Taken from PB";
    }

    if (props.entity.type === "Quality Check") {
      return "Taken from Invoice";
    }
  }

  return "";
});

const computedCessInputHelp = computed(() => {
  if (unref(formData).item_id) {
    if (["Credit Note", "Debit Note"].includes(props.entity.type)) {
      if ([props.entity?.cn_for, props.entity?.dn_for].includes("invoice")) {
        return "Taken from Invoice";
      }

      return "Taken from PB";
    }

    if (props.entity.type === "Quality Check") {
      return "Taken from Invoice";
    }
  }

  return "";
});

const computedRateInputHelp = computed(() => {
  if (unref(formData).item_id) {
    if (["Credit Note", "Debit Note"].includes(props.entity.type)) {
      if ([props.entity?.cn_for, props.entity?.dn_for].includes("invoice")) {
        return "Taken from Invoice";
      }

      return "Taken from PB";
    }

    if (props.entity.type === "Quality Check") {
      return "Taken from Invoice";
    }
  }

  return "";
});

const computedQtyInputHelp = computed(() => {
  if (unref(disableQuantityInput)) {
    return "";
  }

  if (unref(maxQty)) {
    if (
      ["E-Invoice", "Credit Note", "Debit Note", "Quality Check"].includes(
        props.entity.type,
      )
    ) {
      return `Max: ${unref(maxQty)}`;
    }

    if (props.entity.type === "Proof of Delivery") {
      return `Mention the returned quantity, Max: ${unref(
        maxQty,
      )}. Mention in decimals for loose qty.`;
    }
  }

  return "";
});

const computedAmountInputHelp = computed(() => {
  if (unref(formData).item_id) {
    if (["Credit Note", "Debit Note"].includes(props.entity.type)) {
      if ([props.entity?.cn_for, props.entity?.dn_for].includes("invoice")) {
        return "Taken from Invoice";
      }

      return "Taken from PB";
    }

    if (props.entity.type === "Quality Check") {
      return "Taken from Invoice";
    }
  }

  return "";
});

const disableItemSelection = computed(() => {
  return (
    (props.entity.type === "E-Invoice" ||
      props.entity.type === "Proof of Delivery") &&
    unref(itemFormState).edit
  );
});

const disableUnitSelection = computed(() => {
  if (unref(isFreightOrPackageItem)) {
    return true;
  }

  if (unref(isDiscountItem)) {
    return true;
  }

  if (unref(isServiceItem)) {
    return true;
  }

  return [
    "Purchase Order",
    "E-Invoice",
    "Proof of Delivery",
    "Credit Note",
    "Debit Note",
    "Quality Check",
  ].includes(props.entity.type);
});

const disableGstInput = computed(() => {
  return (
    [
      "Purchase Order",
      "Purchase Bill",
      "Credit Note",
      "Debit Note",
      "Quality Check",
    ].includes(props.entity.type) ||
    unref(isDiscountItem) ||
    unref(isServiceItem)
  );
});

const disableCessInput = computed(() => {
  return [
    "Purchase Order",
    "Purchase Bill",
    "E-Invoice",
    "Credit Note",
    "Debit Note",
    "Quality Check",
  ].includes(props.entity.type);
});

const disableRateInput = computed(() => {
  if (unref(formData).is_fixed_rate) {
    return true;
  }

  if (unref(isDiscountItem) || unref(isServiceItem)) {
    return false;
  }

  return ["E-Invoice", "Credit Note", "Debit Note", "Quality Check"].includes(
    props.entity.type,
  );
});

const disableQuantityInput = computed(() => {
  if (unref(isDiscountItem) || unref(isServiceItem)) {
    return true;
  }

  return ["Quality Check"].includes(props.entity.type);
});

const disableAmountInput = computed(() => {
  if (unref(formData).is_fixed_rate) {
    return true;
  }

  if (["Debit Note", "Credit Note"].includes(props.entity.type)) {
    if (
      props.entity?.cn_for === "bill" &&
      props.entity?.type_cd === "increase_purchase"
    )
      return true;

    return ["returns"].includes(props.entity.type_cd);
  }

  return (
    ["E-Invoice"].includes(props.entity.type) &&
    !unref(isDiscountItem) &&
    !unref(isServiceItem)
  );
});

const disableIncTaxCheckBox = computed(() => {
  return unref(isServiceItem);
});

const showHsnInput = computed(() => {
  if (unref(isFreightOrPackageItem)) {
    return false;
  }

  if (unref(isServiceItem)) {
    return true;
  }

  if (unref(isDiscountItem)) {
    return unref(formData).gst_exempted;
  }

  return ![
    "E-Invoice",
    "Proof of Delivery",
    "Credit Note",
    "Debit Note",
    "Quality Check",
  ].includes(props.entity.type);
});

const showGstInput = computed(() => {
  return (
    !["Proof of Delivery"].includes(props.entity.type) &&
    !unref(formData).gst_exempted
  );
});

const showCessInput = computed(() => {
  return (
    (!["E-Invoice", "Proof of Delivery"].includes(props.entity.type) ||
      unref(isDiscountItem) ||
      unref(isServiceItem)) &&
    !unref(formData).gst_exempted
  );
});

const showRateInput = computed(() => {
  if (unref(isDiscountItem) || unref(isServiceItem)) {
    return true;
  }

  return !["E-Invoice", "Proof of Delivery"].includes(props.entity.type);
});

const showAmountInput = computed(() => {
  if (unref(isDiscountItem) || unref(isServiceItem)) {
    return true;
  }

  return !["E-Invoice", "Proof of Delivery"].includes(props.entity.type);
});

const showIncTaxCheckBox = computed(() => {
  if (unref(isDiscountItem) || unref(isServiceItem)) {
    return true;
  }

  return !["E-Invoice", "Credit Note", "Debit Note", "Quality Check"].includes(
    props.entity.type,
  );
});

const showActualRate = computed(() => {
  return (
    !Number.isNaN(Number(unref(formData).rate)) &&
    !["Credit Note", "Debit Note", "Quality Check"].includes(props.entity.type)
  );
});

const showReverseCalculateButton = computed(() => {
  if (["Credit Note", "Debit Note"].includes(props.entity.type)) {
    if (
      props.entity?.cn_for === "bill" &&
      props.entity?.type_cd === "increase_purchase"
    )
      return false;

    return !["returns"].includes(props.entity.type_cd);
  }

  if (unref(isDiscountItem) || unref(isServiceItem)) {
    return true;
  }

  return !["E-Invoice"].includes(props.entity.type);
});

const gstInputWrapperClasses = computed(() => {
  if (
    (["E-Invoice", "Credit Note", "Debit Note", "Quality Check"].includes(
      props.entity.type,
    ) &&
      !unref(isServiceItem)) ||
    !unref(showHsnInput)
  ) {
    return "col-4";
  }

  return "col-2";
});

const cessInputWrapperClasses = computed(() => {
  if (
    ["Credit Note", "Debit Note", "Quality Check"].includes(
      props.entity.type,
    ) ||
    !unref(showHsnInput)
  ) {
    return "col-4";
  }

  return "col-2";
});

const isPoUnitBigger = computed({
  get() {
    if (unref(formData).is_po_unit_bigger) {
      return unref(formData).po_unit;
    } else {
      return unref(formData).unit;
    }
  },
  set(biggerUnit) {
    unref(formData).is_po_unit_bigger = unref(formData).po_unit === biggerUnit;
  },
});

const showPOUnit = computed(() => {
  return (
    !unref(isServiceItem) &&
    !unref(isFreightOrPackageItem) &&
    props.entity.type === "Purchase Bill" &&
    props.source !== "purchases"
  );
});

const showUnitConvertor = computed(() => {
  return (
    !unref(isFreightOrPackageItem) &&
    props.entity.type === "Purchase Bill" &&
    unref(formData).unit !== unref(formData).po_unit
  );
});

const showItemTypeOptions = computed(() => {
  return !itemFormState.value.edit && props.source === "purchases";
});

const onUpdateUnit = () => {
  if (!unref(showUnitConvertor)) {
    unref(formData).conversion_factor = 1;

    unref(formData).is_po_unit_bigger = false;
  }
};

const showConversionInfo = computed(() => {
  return props.entity.type === "Debit Note" && props.source !== "purchases";
});

const conversionFactor = computed(() => {
  if (unref(formData).item_id) {
    if (unref(formData).conversion_factor === 1) {
      return "No difference in PO and PB units";
    }

    if (unref(formData).is_po_unit_bigger) {
      return `1 ${unref(formData).po_unit} = ${
        unref(formData).conversion_factor
      } ${unref(formData).pb_unit}`;
    } else {
      return `1 ${unref(formData).pb_unit} = ${
        unref(formData).conversion_factor
      } ${unref(formData).po_unit}`;
    }
  }

  return "-";
});

let productFilter = ref({});

const initProductFilter = () => {
  const params = {
    isActive: 1,
    orderBy: "asc",
    sortBy: "name",
  };

  if (props.categories) {
    params.categoryNames = props.categories.join(",");
  }

  if (unref(isServiceItem)) {
    params.has_linked_item = 1;
  }

  productFilter.value = useFilter({
    entities: props.items,
    entity: "product",
    options: { params },
  });
};

const onProductFilter = (query, update, abort) => {
  const overrideParams = {
    isActive: 1,
    sortBy: query ? "created_date" : "name",
    orderBy: query ? "desc" : "asc",
  };

  if (["freight", "package"].includes(unref(itemType))) {
    query = unref(itemType);
  }

  productFilter.value.onFilter(query, update, abort, overrideParams);
};

watchEffect(() => {
  if (unref(itemFormState)?.open) {
    initProductFilter();
  }
});

const populateItemData = (item) => {
  const entityType = props.entity.type;
  const itemTypeVal = unref(itemType);
  const isPurchasesSource = unref(props).source === "purchases";

  const cleanFormData = () => {
    delete formData.value._id;

    delete formData.value.gst_rate;
  };

  const setCommonFields = (fields) => {
    formData.value = { ...formData.value, ...fields };

    cleanFormData();
  };

  const setQtyFields = (item, qtyField) => {
    formData.value = {
      ...item,
      [qtyField]: Number(item.qty),
      qty: 1,
    };
  };

  switch (entityType) {
    case "Purchase Order": {
      const { _id, gst_rate } = item;

      setCommonFields({
        amount: 1,
        amount_wt: 1,
        gst: gst_rate,
        is_tax_included: false,
        item_id: _id,
        qty: 1,
        rate: 1,
        rate_wt: 1,
      });

      break;
    }
    case "Proof of Delivery":
      setQtyFields(item, "invc_qty");

      break;
    case "Credit Note":
      setQtyFields(
        item,
        props.entity.cn_for === "bill" ? "pb_qty" : "invc_qty",
      );

      break;
    case "Debit Note": {
      const { qty, rate, unit } = item;

      if (props.entity.dn_for === "bill") {
        formData.value = {
          ...item,
          pb_qty: qty,
          pb_rate: rate,
          pb_unit: unit,
          qty: 1,
        };
      } else {
        formData.value = { ...item, invc_qty: qty, qty: 1 };
      }

      break;
    }
    case "Quality Check":
      setQtyFields(item, "invc_qty");

      break;
    case "Purchase Bill": {
      const { _id, gst_rate } = item;

      if (itemTypeVal === "product" && isPurchasesSource) {
        setCommonFields({
          amount: 1,
          amount_wt: 1,
          gst: gst_rate,
          is_tax_included: false,
          item_id: _id,
          po_unit: "Units",
          po_amount: 0,
          po_rate: 0,
          po_qty: 1,
          qty: 1,
          rate: 1,
          rate_wt: 1,
        });
      } else if (unref(isFreightOrPackageItem)) {
        formData.value = {
          ...item,
          conversion_factor: 1,
          gst: gst_rate,
          is_po_unit_bigger: false,
          is_tax_included: false,
          item_id: _id,
          po_unit: "Units",
          po_amount: 0,
          po_rate: 0,
          po_qty: 1,
          qty: 1,
        };

        cleanFormData();
      }

      break;
    }

    default:
      break;
  }

  // Discount item logic
  if (unref(isDiscountItem)) {
    const { _id, gst_rate, unit } = item;

    setCommonFields({
      amount: 1,
      amount_wt: 1,
      gst: gst_rate,
      item_id: _id,
      qty: 1,
      rate: 1,
      rate_wt: 1,
      conversion_factor: 1,
      pb_gst: 0,
      pb_rate: 1,
      pb_cess: 0,
      pb_qty: 1,
      po_amount: 1,
      po_qty: 1,
      converted_qty: 1,
      po_rate: 1,
      is_po_unit_bigger: true,
      pb_unit: unit,
      po_unit: unit,
      is_tax_included: true,
    });
  }

  if (unref(isServiceItem)) {
    const { _id, gst_rate, min_price } = item;

    setCommonFields({
      amount: min_price,
      amount_wt: min_price,
      category_name: "VAS",
      gst: gst_rate,
      is_tax_included: true,
      item_id: _id,
      qty: 1,
      rate: min_price,
      rate_wt: min_price,
    });
  }

  if (
    [
      "Proof of Delivery",
      "Credit Note",
      "Debit Note",
      "Quality Check",
    ].includes(entityType)
  ) {
    updateAmount();
  } else {
    updateRate();
  }
};

const { uoms } = storeToRefs(useUomStore());

const unitFilter = useFilter({
  entities: uoms,
  entity: "uom",
});

const updateRate = () => {
  let item = unref(formData);

  if (unref(computedIsTaxIncluded)) {
    unref(formData).rate_wt = Number(item.rate);
  } else {
    unref(formData).rate = Number(item.rate_wt);
  }

  updateAmount();
};

const updateAmount = () => {
  let item = unref(formData);

  let gst = Number(item?.gst) || 0;
  let cess = Number(item?.cess) || 0;

  if (unref(computedIsTaxIncluded)) {
    unref(formData).rate = Number(item.rate_wt) / (1 + (gst + cess) / 100);
  } else {
    unref(formData).rate_wt =
      Number(item.rate) + Number(item.rate) * ((gst + cess) / 100);
  }

  unref(formData).amount = Number(item?.qty) * Number(item?.rate);

  unref(formData).amount_wt = Number(item?.qty) * Number(item?.rate_wt);

  if (unref(isFreightOrPackageItem)) {
    updateFreightItem();
  }
};

const updateFreightItem = () => {
  const item = unref(formData);

  const { amount, amount_wt, rate, rate_wt } = item;

  formData.value = {
    ...item,
    po_amount: amount,
    po_amount_wt: amount_wt,
    po_rate: rate,
    po_rate_wt: rate_wt,
  };
};

const isReverseCalculated = ref(true);

const onUpdateAmount = () => {
  if (unref(showReverseCalculateButton)) {
    isReverseCalculated.value = false;
  }
};

const reverseCalculate = () => {
  let item = unref(formData);

  if (item?.qty > 0) {
    if (unref(computedIsTaxIncluded) && Number(item.amount_wt)) {
      unref(formData).rate_wt = Number(item.amount_wt) / Number(item.qty);
    } else if (!unref(computedIsTaxIncluded) && Number(item.amount)) {
      unref(formData).rate = Number(item.amount) / Number(item.qty);
    } else {
      unref(formData).rate = 0;

      unref(formData).rate_wt = 0;

      if (unref(computedIsTaxIncluded)) {
        unref(formData).amount = 0;
      } else {
        unref(formData).amount_wt = 0;
      }
    }
  }

  isReverseCalculated.value = true;

  validateForm();

  updateAmount();
};

const addButton = computed(() => {
  return {
    label: unref(itemFormState).edit ? "Save" : "Add",
  };
});

const onSubmit = () => {
  if (props.toggle.method) {
    props.toggle.method({
      index: props.index,
      item: unref(formData),
    });

    itemType.value = "product";

    closeForm(true);
  }
};

const isFreightOrDiscountItem = computed(
  () => unref(isFreightOrPackageItem) || unref(isDiscountItem),
);

const computedMaxVal = computed(() => {
  return Infinity;
});

const computedMinRate = computed(() => {
  return unref(isFreightOrDiscountItem) ? -Infinity : 0;
});

const computedMinAmount = computed(() => {
  return unref(isFreightOrDiscountItem) ? -Infinity : 1;
});

watch(
  () => props.item,
  (newItem) => {
    const name = newItem?.name?.toLowerCase() || "";

    if (name.includes("freight")) itemType.value = "freight";
    else if (name.includes("package")) itemType.value = "package";
    else itemType.value = "product";
  },
  {
    immediate: true,
    deep: true,
  },
);
</script>
